import { de } from './de.js';

/** Zeitzone aller Anzeigen (Kalendertage und Uhrzeiten folgen Berlin, nicht UTC). */
export const TIME_ZONE = 'Europe/Berlin';
const LOCALE = 'de-DE';

const numberFormat = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 });
const percentFormat = new Intl.NumberFormat(LOCALE, { style: 'percent', maximumFractionDigits: 0 });
const dateFormat = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: TIME_ZONE,
});
const timeFormat = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: TIME_ZONE,
});
const relativeFormat = new Intl.RelativeTimeFormat('de', { numeric: 'auto' });

export type DateInput = Date | string;

const toDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value));

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Zahl mit Tausenderpunkt und höchstens einer Nachkommastelle („1.134“). */
const number = (value: number): string => numberFormat.format(value);

/** Anteil (0 bis 1) als Prozentwert mit geschütztem Leerzeichen („40 %“). */
const percent = (fraction: number): string => percentFormat.format(fraction);

/** Kalendertag in Berliner Zeit („28. Nov. 2026“). */
const date = (value: DateInput): string => dateFormat.format(toDate(value));

/** Uhrzeit in Berliner Zeit („14:05“). */
const time = (value: DateInput): string => timeFormat.format(toDate(value));

/**
 * Abstand zu `now` in Worten („vor 3 Wochen“, „gestern“, „in 3 Tagen“). Es wird immer abgerundet, damit
 * „vor 3 Wochen“ nie schon nach 2,6 Wochen erscheint.
 */
function relative(value: DateInput, now: Date = new Date()): string {
  const diff = toDate(value).getTime() - now.getTime();
  const sign = Math.sign(diff);
  const abs = Math.abs(diff);
  if (abs < MINUTE) {
    return de.format.justNow;
  }
  if (abs < HOUR) {
    return relativeFormat.format(sign * Math.floor(abs / MINUTE), 'minute');
  }
  if (abs < DAY) {
    return relativeFormat.format(sign * Math.floor(abs / HOUR), 'hour');
  }
  if (abs < 14 * DAY) {
    return relativeFormat.format(sign * Math.floor(abs / DAY), 'day');
  }
  if (abs < 60 * DAY) {
    return relativeFormat.format(sign * Math.floor(abs / (7 * DAY)), 'week');
  }
  if (abs < 365 * DAY) {
    return relativeFormat.format(sign * Math.floor(abs / (30 * DAY)), 'month');
  }
  return relativeFormat.format(sign * Math.floor(abs / (365 * DAY)), 'year');
}

/** „1 Tag“, „3 Tage“ (Nominativ). */
const days = (count: number): string => `${number(count)} ${count === 1 ? 'Tag' : 'Tage'}`;
/** „in 1 Tag“, „in 3 Tagen“ (Dativ, nach „in“, „seit“, „vor“). */
const daysDative = (count: number): string => `${number(count)} ${count === 1 ? 'Tag' : 'Tagen'}`;
/** „1 Stunde“, „5 Stunden“. */
const hours = (count: number): string => `${number(count)} ${count === 1 ? 'Stunde' : 'Stunden'}`;
/** Dativ und Nominativ fallen bei Stunden zusammen. */
const hoursDative = hours;

export const format = {
  number,
  percent,
  date,
  time,
  relative,
  days,
  daysDative,
  hours,
  hoursDative,
} as const;
